import { Incident, IncidentAmendment, IncidentEvent } from "./types";

/** Fixture hash chain. The live server returns SHA-256; this only links local records. */
export function chainHash(prev: string, body: string): string {
  let hash = 2166136261;
  const source = `${prev}|${body}`;
  for (let i = 0; i < source.length; i += 1) {
    hash ^= source.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return `00000000${(hash >>> 0).toString(16)}`.slice(-8);
}

export function eventBody(event: IncidentEvent): string {
  return JSON.stringify({
    type: event.type,
    at: event.at,
    actor: event.actor_user_id,
    payload: event.payload || {}
  });
}

export function amendmentBody(amendment: IncidentAmendment): string {
  return JSON.stringify({
    reason: amendment.reason,
    at: amendment.at,
    actor: amendment.author_user_id,
    changes: amendment.changes
  });
}

export function sealChain(incident: Incident): Incident {
  let prev = "";
  incident.events.forEach((event) => {
    const hash = chainHash(prev, eventBody(event));
    event.prev_hash = prev;
    event.hash = hash;
    prev = hash;
  });
  incident.amendments.forEach((amendment) => {
    const hash = chainHash(prev, amendmentBody(amendment));
    amendment.prev_hash = prev;
    amendment.hash = hash;
    prev = hash;
  });
  incident.hash = prev;
  return incident;
}

export function appendEvent(incident: Incident, event: IncidentEvent): void {
  const prev = incident.hash || "";
  event.prev_hash = prev;
  event.hash = chainHash(prev, eventBody(event));
  incident.hash = event.hash;
  incident.events.push(event);
}

export function appendAmendment(incident: Incident, amendment: IncidentAmendment): void {
  const prev = incident.hash || "";
  amendment.prev_hash = prev;
  amendment.hash = chainHash(prev, amendmentBody(amendment));
  incident.hash = amendment.hash;
  incident.amendments.push(amendment);
}

export function verifyChain(incident: Incident): boolean {
  const nodes = [
    ...incident.events.map((event) => ({
      prev: event.prev_hash || "",
      hash: event.hash || "",
      body: eventBody(event)
    })),
    ...incident.amendments.map((amendment) => ({
      prev: amendment.prev_hash || "",
      hash: amendment.hash || "",
      body: amendmentBody(amendment)
    }))
  ];
  if (!nodes.length) return !incident.hash;
  const byPrev = new Map(nodes.map((node) => [node.prev, node]));
  let prev = "";
  const seen = new Set<string>();
  while (byPrev.has(prev)) {
    const node = byPrev.get(prev)!;
    if (!node.hash || seen.has(node.hash)) return false;
    if (chainHash(node.prev, node.body) !== node.hash) return false;
    seen.add(node.hash);
    prev = node.hash;
  }
  return seen.size === nodes.length && prev === (incident.hash || "");
}
